/*************************
 * Author : Matthieu ROBIN
 *************************/
import axiosInstance from "../axios";
import { Car } from "../models/car";
import { v4 as uuidv4 } from "uuid";
import url from "url";
import { getAuthorizationHeader } from "../helpers/auth";
import { Filters } from "../models/filter";
import { CarsDTO } from "../models/dto";

export class CarService {
  static getCars(filters?: Filters): Promise<CarsDTO> {
    const filterQuery = filters
      ? Object.entries(filters)
          .filter(([_, v]) => v !== undefined)
          .map(([k, v]) => `${k}=${v}`)
          .join("&")
      : "";

    return axiosInstance
      .get<CarsDTO>(`/cars?${filterQuery}`, {
        headers: getAuthorizationHeader(),
      })
      .then((response) => response.data);
  }

  static createCar(car: Car): Promise<Car> {
    return axiosInstance
      .post("/cars", car, { headers: getAuthorizationHeader() })
      .then((response) => response.data);
  }

  static updateCar(id: string, car: Car): Promise<Car> {
    return axiosInstance
      .put(`/cars/${id}`, car, { headers: getAuthorizationHeader() })
      .then((response) => response.data);
  }

  static deleteCar(id: string): Promise<string> {
    return axiosInstance
      .delete(`/cars/${id}`, { headers: getAuthorizationHeader() })
      .then(() => id);
  }

  static sellCar(
    id: string,
    updatedAt: Date,
    sellType: string,
    sellPrice: number,
  ): Promise<Car> {
    return axiosInstance
      .post(
        `/cars/${id}/sell`,
        { updatedAt, sellType, sellPrice },
        { headers: getAuthorizationHeader() },
      )
      .then((response) => response.data);
  }

  static cancelSale(id: string, updatedAt: Date): Promise<Car> {
    return axiosInstance
      .post(
        `/cars/${id}/cancelsale`,
        { updatedAt },
        { headers: getAuthorizationHeader() },
      )
      .then((response) => response.data);
  }

  static async uploadImage(imageURL: string): Promise<string> {
    const imageData = await fetch(imageURL).then((result) => result.blob());
    const presignedUrl = await this.requestImageUpload(
      uuidv4(),
      imageData.type,
    );
    await axiosInstance.put(presignedUrl, imageData, {
      headers: { "Content-Type": imageData.type },
    });
    const parsedUrl = url.parse(presignedUrl);
    const { protocol, hostname, pathname } = parsedUrl;
    return `${protocol}//${hostname}${pathname}`;
  }

  private static requestImageUpload(
    key: string,
    mimeType: string,
  ): Promise<string> {
    return axiosInstance
      .post(
        `/cars/images/request-upload`,
        { key, mimeType },
        { headers: getAuthorizationHeader() },
      )
      .then((response) => response.data.url);
  }

  static syncCars(): Promise<void> {
    return axiosInstance
      .post("/cars/sync", {}, { headers: getAuthorizationHeader() })
      .then((response) => response.data);
  }
}
